import { UpsellForm } from '@/components/form/upsell/upsell-form';
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { upsellFormSchema, type UpsellFormData } from '@/components/form/upsell/validation';
import { BackButton } from '@/components/ui/BackButton';
import { Button } from '@/components/ui/button';
import { ErrorDisplay } from '@/components/ui/error-display';
import { useToast } from '@/components/ui/use-toast';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { UpdateUpsellInput, UpsellDetails, useUpdateUpsell, useUpsell } from '@/lib/api/upsells';
import { parseTRPCError, type ParsedTRPCError } from '@/lib/utils/trpc-error-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { UpsellEditSkeleton } from './components/upsell-edit-skeleton';
import { UpsellNotFound } from './components/upsell-not-found';

/**
 * Page for editing an existing upsell
 */

const EditUpsellPageInner = () => {
  const { upsellId } = useParams<{ upsellId: string }>();
  if (!upsellId) {
    throw new Error('Upsell ID is required');
  }

  const { data: upsell, isLoading, error } = useUpsell(upsellId);
  if (isLoading) {
    return <UpsellEditSkeleton />;
  }

  if (error) {
    return <ErrorDisplay error={parseTRPCError(error)} />;
  }
  if (!upsell) {
    return <UpsellNotFound />;
  }
  return <EditUpsellPageContent upsell={upsell} />;
};

interface EditUpsellPageContentProps {
  upsell: UpsellDetails;
}

const EditUpsellPageContent = ({ upsell }: EditUpsellPageContentProps) => {
  const { t } = useTranslation('upsells');
  const { toast } = useToast();
  const { id: storeId, navigate, storeRoutes } = useStoreNavigate();
  const updateUpsellMutation = useUpdateUpsell();
  const [formError, setFormError] = React.useState<ParsedTRPCError | null>(null);
  // Determine triggerType from triggers: if first trigger has type 'product', it's 'product', otherwise 'any'
  const determinedTriggerType =
    upsell.triggers?.length > 0 && upsell.triggers[0]?.type === 'product' ? 'product' : 'any';

  const methods = useForm<any>({
    defaultValues: {
      offerTitle: upsell.offerTitle,
      type: upsell.type,
      enabled: upsell.enabled,
      triggerType: determinedTriggerType,
      triggers:
        determinedTriggerType === 'any'
          ? [{ productId: null, type: 'any' }]
          : upsell.triggers
              ?.filter((t) => t.type === 'product')
              .map((trigger) => ({
                productId: trigger.productId,
                type: trigger.type,
              })) || [],
      // Load offers for type='upsell'
      offers:
        upsell.type === 'upsell'
          ? upsell.offers?.map((offer: any) => ({
              productId: offer.productId,
              variantId: offer.variantId || null,
              priceId: offer.priceId || null,
              title: offer.title || '',
              titleTrans: offer.titleTrans || { en: '' },
            })) || []
          : [],
      // Load orderBumpOffers for type='orderbump'
      orderBumpOffers:
        upsell.type === 'orderbump'
          ? upsell.orderBumpOffers?.map((offer: any) => ({
              id: offer.id,
              productId: offer.productId,
              variantId: offer.variantId || null,
              priceId: offer.priceId || null,
              type: offer.type || 'primary',
              precheck: offer.precheck || false,
              quantity: offer.quantity || 1,
              titleTrans: offer.titleTrans || {},
              descriptionTrans: offer.descriptionTrans || {},
              overrideImageUrl: offer.overrideImageUrl || null,
              displayPrice: offer.displayPrice ?? true,
              displayCompareAtPrice: offer.displayCompareAtPrice ?? false,
              compareAtPriceDiscount: offer.compareAtPriceDiscount || 20,
              rules: offer.rules || [],
            })) || []
          : [],
    },
    resolver: zodResolver(upsellFormSchema) as any,
    mode: 'onBlur',
  });
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;
  const handleUpdateUpsell = async (validatedData: UpsellFormData) => {
    try {
      setFormError(null);
      const input: UpdateUpsellInput = {
        id: upsell.id,
        data: {
          offerTitle: validatedData.offerTitle,
          enabled: validatedData.enabled,
          type: validatedData.type,
          triggers: validatedData.triggers.map((trigger: any) => ({
            productId: trigger.productId,
            type: trigger.type || 'product',
          })),
          // Only send the relevant offers array based on type
          offers:
            validatedData.type === 'upsell'
              ? validatedData.offers.map((offer: any) => ({
                  productId: offer.productId,
                  variantId: offer.variantId,
                  priceId: offer.priceId,
                  title: offer.title || '',
                  titleTrans: offer.titleTrans || { en: '' },
                }))
              : [],
          orderBumpOffers:
            validatedData.type === 'orderbump'
              ? validatedData.orderBumpOffers.map((offer: any) => ({
                  ...(offer.id ? { id: offer.id } : {}),
                  productId: offer.productId,
                  variantId: offer.variantId,
                  priceId: offer.priceId,
                  type: offer.type,
                  precheck: offer.precheck,
                  quantity: offer.quantity,
                  titleTrans: offer.titleTrans,
                  descriptionTrans: offer.descriptionTrans,
                  overrideImageUrl: offer.overrideImageUrl,
                  displayPrice: offer.displayPrice,
                  displayCompareAtPrice: offer.displayCompareAtPrice,
                  compareAtPriceDiscount: offer.compareAtPriceDiscount,
                  rules: offer.rules || [],
                }))
              : [],
        },
      };
      await updateUpsellMutation.mutateAsync(input);

      toast({
        title: t('error.success'),
        description: t('error.updatedSuccess'),
      });
      navigate(storeRoutes.UPSELLS());
    } catch (error) {
      const parsedError = parseTRPCError(error);
      setFormError(parsedError);
      if (!parsedError.isValidationError) {
        toast({
          title: t('error.error'),
          description: parsedError.message,
          variant: 'destructive',
        });
      }
    }
  };

  return (
    <div className="bg-background min-h-screen">
      <div className="flex flex-col space-y-4 sm:space-y-6">
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(handleUpdateUpsell)}>
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BackButton to={storeRoutes.UPSELLS()} />
                <div>
                  <h1 className="text-2xl font-semibold">{t('edit.title')}</h1>
                  <p className="text-muted-foreground text-sm">{t('edit.description')}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(storeRoutes.UPSELLS())}
                  disabled={isSubmitting}
                >
                  {t('actions.cancel')}
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? t('actions.updating') : t('actions.update')}
                </Button>
              </div>
            </div>

            {formError && !formError.isValidationError && (
              <div className="mb-6">
                <ErrorDisplay error={formError} />
              </div>
            )}
            <UpsellForm />
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export const EditUpsellPage = withRequiredPermission(EditUpsellPageInner, PERMISSIONS.STORES_MANAGE);
export default EditUpsellPage;
